import { Children, cloneElement, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useURLQuery } from '../../Common/Hook/urlQuery';
import { useApplicationsBulkUploadPostQuery } from '../../Common/Queries/applicationsQueries';
import { usePositionsGetQuery } from '../../Common/Queries/positionsQueries';
import {
  useCalendarServicePutRefreshTokenQuery,
  useServicePostQuery,
} from '../../Common/Queries/serviceQueries';
import { useTeamGetQueryById } from '../../Common/Queries/teamQueries';
import { useUserGetQueryById } from '../../Common/Queries/usersQueries';

import { decodeJwt, isLogin, isValidID } from '../../utils';

import { ALL_DATA_LIMIT, DURATION_TIME, TEAM_REQUIREMENTS_END_POINT } from '../../constants';

export const convertType = (type, value) => {
  if (['number', 'linear scale', 'rating'].includes(type)) {
    return Number(value);
  }
  if (type === 'check box') {
    return value.split(';');
  }
  return value;
};
export const formatData = (value, order = []) => {
  return {
    name: value?.name,
    phone: `+91${value?.phone}`,
    email: value?.email,
    position: value?.position,
    isUpload: false,
    customFormData: [
      ...(function customFormData() {
        const result = Object.entries(value)
          ?.filter((v) => !['email', 'phone', 'name', 'position'].includes(v[0]))
          ?.reduce((initialiValue, obj) => {
            return { [obj[0]]: obj[1], ...initialiValue };
          }, {});
        const resultArr = order?.map((obj) => {
          return {
            fieldName: obj?.fieldName,
            ...(result[obj?.fieldName?.toLowerCase()] && {
              fieldValue: convertType(
                obj?.fieldType?.toLowerCase(),
                result[obj?.fieldName?.toLowerCase()]
              ),
            }),
          };
        });
        return resultArr;
      })(),
    ],
  };
};

export function ToolbarContainer({ children }) {
  const urlQuery = useURLQuery();
  const location = useLocation().pathname;
  const { teamName } = useLocation().state ?? {};
  const urlPath = useLocation();
  const queryClient = useQueryClient();
  const putRefrehToken = useCalendarServicePutRefreshTokenQuery({
    onSuccess: () => {
      queryClient.invalidateQueries(['userGetQueryById']);
    },
  });

  const POSITIONS_NEEDED_ROUTES = ['/applications', '/calendar', '/referrals', '/add-application'];
  const needsPositions = POSITIONS_NEEDED_ROUTES.some((r) => location.startsWith(r));

  const positionList = usePositionsGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && needsPositions,
      refetchInterval: needsPositions ? DURATION_TIME : false,
      srefetchIntervalInBackground: false,
    }
  );
  const queryParams = new URLSearchParams(urlPath.search);
  const queryParamString = urlPath.search;

  const accessToken = JSON.parse(localStorage.getItem('loggedInUserInfo'))?.accessToken;

  const { entityID: userId } = decodeJwt(accessToken) || {};

  useEffect(() => {
    const handleServiceRefreshToken = async () => {
      const code = queryParams.get('code');
      if (code && location !== '/settings') {
        const service = queryParams.get('scope');
        try {
          await putRefrehToken.mutateAsync({
            reqBody: { exchangeCode: code, scope: service.split(' ') },
            userId,
          });
        } catch (error) {
          console.error('Error refreshing token:', error);
        }
        window.history.replaceState(null, '', window.location.pathname);
      } else if (queryParams.get('error')) window.close();
    };

    handleServiceRefreshToken();
  }, [queryParamString]);

  const teamId =
    location === TEAM_REQUIREMENTS_END_POINT &&
    isValidID(urlQuery.get('teamId')) &&
    urlQuery.get('teamId');

  const teamData = useTeamGetQueryById({ reqId: teamId }, { enabled: !teamName && teamId > 0 });
  const userData = useUserGetQueryById(userId, { enabled: userId > 0 });
  const postServices = useServicePostQuery();

  const postApplications = useApplicationsBulkUploadPostQuery();
  const postBulkApplications = async (reqBody, order) => {
    const formatedReqBody = formatData(reqBody, order);
    const data = await postApplications.mutateAsync(formatedReqBody);
    return { status: 'success', data };
  };

  const child = Children.only(children);
  return cloneElement(child, {
    teamData,
    userData,
    postBulkApplications,
    postServices,
    positionList,
  });
}
